// Output is printed on small products / offered as a download, so full
// phone-camera resolution is unnecessary and only slows JPEG encoding.
const MAX_OUTPUT_DIM = 2400;

const BORDER_ANGLES_THIN = [0, 45, 90, 135, 180, 225, 270, 315];
const BORDER_ANGLES_THICK = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

function loadImage(uri) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = uri;
    });
}

function createCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

// The downscaled shape rendered as a clean mask. Some shape PNGs bake faint
// internal lines into their alpha channel (near- but not fully-opaque); those
// are snapped fully opaque so they don't bleed through, while a thin
// anti-aliased fringe is kept on the outer edge.
function buildMask(shapeImg, width, height) {
    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(shapeImg, 0, 0, width, height);

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    for (let i = 3; i < data.length; i += 4) {
        const alpha = data[i];
        data[i] = alpha >= 128 ? 255 : alpha * 2;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
}

// A solid silhouette of the mask filled with `color`, transparent outside.
function buildColoredSilhouette(maskCanvas, width, height, color) {
    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(maskCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    const alpha = (color.a == null ? 255 : color.a) / 255;
    ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    ctx.fillRect(0, 0, width, height);
    return canvas;
}

export default async function shapen(imageUri, shapeUri, canvas, options) {
    try {
        const [image, shape] = await Promise.all([
            loadImage(imageUri),
            loadImage(shapeUri)
        ]);

        const shapeRatio = shape.height / shape.width;
        const sourceRatio = image.height / image.width;

        // Base center-crop of the source photo to the shape's aspect ratio.
        let baseCropW, baseCropH;
        if (sourceRatio > shapeRatio) {
            baseCropW = image.width;
            baseCropH = Math.round(image.width * shapeRatio);
        } else {
            baseCropH = image.height;
            baseCropW = Math.round(image.height / shapeRatio);
        }

        // Framing controls — let the user shift and zoom the source within the
        // shape's silhouette. `zoom` >= 1 narrows the crop window (zoom in).
        // `offsetX`/`offsetY` in [-1, 1] pan the window within the available
        // slack: 0 = centered (default), -1 = hard left/top, 1 = hard right/bottom.
        const framing = (options && options.framing) || {};
        const zoom = Math.max(1, framing.zoom || 1);
        const offsetX = Math.max(-1, Math.min(1, framing.offsetX || 0));
        const offsetY = Math.max(-1, Math.min(1, framing.offsetY || 0));

        const cropW = Math.max(1, Math.min(image.width, Math.round(baseCropW / zoom)));
        const cropH = Math.max(1, Math.min(image.height, Math.round(baseCropH / zoom)));
        const slackX = image.width - cropW;
        const slackY = image.height - cropH;
        const cropX = Math.round(slackX * (0.5 + offsetX * 0.5));
        const cropY = Math.round(slackY * (0.5 + offsetY * 0.5));

        // Cap the output resolution. Output stays sized to the base (un-zoomed)
        // crop so zooming-in doesn't degrade the rendered resolution.
        let width = baseCropW;
        let height = baseCropH;
        const longest = Math.max(width, height);
        if (longest > MAX_OUTPUT_DIM) {
            const scale = MAX_OUTPUT_DIM / longest;
            width = Math.max(1, Math.round(width * scale));
            height = Math.max(1, Math.round(height * scale));
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Canvas starts fully transparent — the shape silhouette (and optional
        // border) is everything the result contains, so it prints cleanly on
        // dark cups, t-shirts, hoodies etc.

        const mask = buildMask(shape, width, height);

        // Border: a colored ring hugging the outside of the silhouette,
        // produced by stamping the colored silhouette around a small circle.
        const borderWidth = options && options.border && options.border.width;
        if (borderWidth) {
            const borderColor = options.border.color || { r: 0, g: 0, b: 0, a: 255 };
            const silhouette = buildColoredSilhouette(mask, width, height, borderColor);
            const angles = borderWidth <= 3 ? BORDER_ANGLES_THIN : BORDER_ANGLES_THICK;
            for (const deg of angles) {
                const rad = (deg * Math.PI) / 180;
                ctx.drawImage(
                    silhouette,
                    Math.round(borderWidth * Math.cos(rad)),
                    Math.round(borderWidth * Math.sin(rad)),
                    width,
                    height
                );
            }
        }

        // Mask the cropped photo to the shape, then lay it over the border.
        const photo = createCanvas(width, height);
        const photoCtx = photo.getContext('2d');
        photoCtx.imageSmoothingEnabled = true;
        photoCtx.imageSmoothingQuality = 'high';
        photoCtx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, width, height);
        photoCtx.globalCompositeOperation = 'destination-in';
        photoCtx.drawImage(mask, 0, 0);
        ctx.drawImage(photo, 0, 0);

        return {
            // PNG preserves the alpha channel so the silhouette sits on top
            // of any product background without a white halo around it.
            data: canvas.toDataURL('image/png'),
            canvas,
            width,
            height
        };
    } catch (e) {
        console.error('ERROR: ' + (e && e.message ? e.message : JSON.stringify(e)));
        throw e;
    }
}
